/**
 * "Page not found" — the answer for an address the app has no page for
 * (Deep-test DSH-043).
 *
 * The catch-all used to <Navigate> to /dashboard: a mistyped URL or a broken
 * link in an email silently became the dashboard, as if the app had ignored
 * the person, and the address they followed was gone from history. This names
 * the address, says plainly that nothing lives there, and offers the way back
 * — the same shape as NoAccess, its sibling for "it exists, but not for you".
 */
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export default function NotFound() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center px-4 py-20 text-center" data-testid="not-found">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface-muted">
        <MagnifyingGlassIcon className="h-7 w-7 text-content-subtle" aria-hidden="true" />
      </div>
      <h1 className="text-lg font-semibold text-content">Page not found</h1>
      <p className="mt-2 max-w-md text-sm text-content-muted">
        There is no page at this address. It may have been mistyped, or the link that brought you
        here may be out of date.
      </p>
      <p className="mt-3 max-w-md break-all rounded bg-surface-subtle px-3 py-1.5 font-mono text-xs text-content-secondary">
        {pathname}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link to="/dashboard" className="btn-primary">Go to your dashboard</Link>
        <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    </div>
  );
}
